import type { VersionChanges, FieldChange } from '../types'

/** Format a single field change to human-readable text */
function formatFieldChange(field: string, value: string | number | boolean | FieldChange): string {
  const label = field.replace(/([A-Z])/g, ' $1').replace(/^./, (c) => c.toUpperCase())

  if (typeof value === 'object' && value !== null && ('old' in value || 'new' in value)) {
    const fc = value as FieldChange
    if (fc.old !== undefined && fc.new !== undefined) {
      return `${label} changed from '${String(fc.old)}' to '${String(fc.new)}'`
    }
    if (fc.new !== undefined) {
      return `${label} set to '${String(fc.new)}'`
    }
    if (fc.old !== undefined) {
      return `${label} removed (was '${String(fc.old)}')`
    }
    return `${label} updated`
  }

  return `${label} set to '${String(value)}'`
}

/** Parse raw changes (JSON string or VersionChanges map) into human-readable lines */
export function formatChanges(changes: string | VersionChanges | undefined): string[] {
  if (!changes) return ['No details available']

  let parsed: VersionChanges
  if (typeof changes === 'string') {
    try {
      parsed = JSON.parse(changes) as VersionChanges
    } catch {
      return [changes]
    }
  } else {
    parsed = changes
  }

  const lines: string[] = []
  for (const [field, value] of Object.entries(parsed)) {
    lines.push(formatFieldChange(field, value))
  }

  return lines.length > 0 ? lines : ['Changes recorded']
}

/** Format a relative timestamp like "5 minutes ago", "2 hours ago", "3 days ago" */
export function formatRelativeTime(dateStr: string): string {
  const date = new Date(dateStr)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffSec = Math.floor(diffMs / 1000)
  const diffMin = Math.floor(diffSec / 60)
  const diffHour = Math.floor(diffMin / 60)
  const diffDay = Math.floor(diffHour / 24)

  if (diffSec < 60) return 'just now'
  if (diffMin < 60) return `${diffMin} minute${diffMin !== 1 ? 's' : ''} ago`
  if (diffHour < 24) return `${diffHour} hour${diffHour !== 1 ? 's' : ''} ago`
  if (diffDay < 30) return `${diffDay} day${diffDay !== 1 ? 's' : ''} ago`
  return date.toLocaleDateString()
}
